"use client";

import { useRef, useState } from "react";
import styles from "../mojumoncrimson.module.css";
import { ART, STARTERS, type StarterId } from "./data";
import { c } from "./hooks";
import HeroConsole, { type HeroApi } from "./HeroConsole";
import Parade from "./Parade";
import Tamers from "./Tamers";
import FusionLab from "./FusionLab";

const BETA_URL = "https://docs.google.com/document/d/1S7tYD2UCkKMQT8nBPb38VxuXzmDAshCk0HoY075JpmA/edit?usp=sharing";

// Port of MojumonCrimsonFixedStandalone.html. The prototype's own top bar
// (brand + "Beta Build" chip) is dropped — the site header carries the page,
// and the "Beta" tag lives on its nav entry.

export default function MojumonCrimsonPage() {
  const [current, setCurrent] = useState(0);
  const [artOpen, setArtOpen] = useState(false);
  const hero = useRef<HeroApi | null>(null);
  const consoleRef = useRef<HTMLDivElement | null>(null);
  // Beta access = a Google Doc of instructions (owner, 2026-10-09); both
  // buttons open it in a new tab. Replaces the old "Beta Portal Loading" dialog.
  const chooseCard = (id: StarterId) => {
    hero.current?.select(id);
    consoleRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  const starter = STARTERS[current];

  return (
    <div className={`${styles.page} ${c(artOpen && "art-open")}`}>
      <div className={c("site-shell")}>
        <main id="top">
          <section className={c("hero")}>
            <div className={c("hero-copy")}>
              <p className={c("eyebrow", "pixel-font")}>A Dojo-born monster-taming RPG</p>
              <h1>Catch. Train. <span className={c("crimson")}>Conquer.</span></h1>
              <p>Enter a world where the <strong>Dojo community becomes the adventure.</strong> Catch creatures called Mojumon, build your perfect team, level them up, and battle your way through the Crimson journey.</p>
              <div className={c("actions")}>
                <a className={c("btn", "btn-primary")} href={BETA_URL} target="_blank" rel="noopener noreferrer">Play the Beta <span>▶</span></a>
                <a className={c("btn", "btn-secondary")} href="#starters">Choose a Starter</a>
              </div>
              <div className={c("micro", "pixel-font")}><span>Monster Taming</span><span>Dojo Tamers</span><span>GBA Soul</span></div>
            </div>
            <HeroConsole current={current} setCurrent={setCurrent} apiRef={hero} consoleRef={consoleRef} />
          </section>

          <Parade onArtOpen={setArtOpen} />

          <section className={c("section", "starters")} id="starters">
            <div className={c("section-inner")}>
              <p className={c("section-kicker", "pixel-font")}>Your first partner</p>
              <h2>Choose Your Starter</h2>
              <p className={c("section-intro")}>Every great Tamer starts with one decision. Pick the Mojumon that matches your style—and begin a story the Dojo has never seen before.</p>
              <div className={c("starter-grid")}>
                {STARTERS.map((s, i) => (
                  <article key={s.id} className={c("starter-card", s.id, s.id === starter.id && "selected")} tabIndex={0}
                    onClick={() => chooseCard(s.id)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); chooseCard(s.id); } }}>
                    <div className={c("sprite-window")}><img src={s.src} alt={`${s.display} in 16-bit pixel art`} /></div>
                    <span className={c("dex-number")}>STARTER {String(i + 1).padStart(3, "0")}</span>
                    <h3 className={c("starter-name")}>{s.display}</h3>
                    <span className={c("type")}>{s.type}</span>
                    <p>{s.blurb}</p>
                  </article>))}
              </div>
            </div>
          </section>

          <section className={c("section", "heroes")} aria-labelledby="heroesTitle">
            <div className={c("section-inner")}>
              <div className={c("heroes-panel")}>
                <div className={c("heroes-art")}><img src={ART.heroes} alt="The two playable Mojumon Crimson protagonists in 16-bit pixel art" /></div>
                <div className={c("heroes-copy")}>
                  <p className={c("section-kicker", "pixel-font")}>The heroes you control</p>
                  <h2 id="heroesTitle">Choose Your Tamer</h2>
                  <p>Step into the Crimson world as one of its lead Tamers. Carry your field scroll, choose your first partner, and shape the journey through every encounter and battle.</p>
                  <div className={c("hero-tags")}><span>Playable Protagonists</span><span>Field Scrolls</span><span>Your Journey</span></div>
                </div>
              </div>
            </div>
          </section>

          <section className={c("section", "loop")}>
            <div className={c("section-inner")}>
              <p className={c("section-kicker", "pixel-font")}>Build your dream party</p>
              <h2>The Mojumon Journey</h2>
              <p className={c("section-intro")}>Every encounter can change your team. Every battle can reveal a new favorite.</p>
              <div className={c("loop-grid")}>
                {[["01", "◉", "Catch", "Explore the world and capture new Mojumon to expand your roster."], ["02", "✦", "Train", "Learn what makes each Mojumon special and build a team around your style."], ["03", "↑", "Level Up", "Grow stronger, unlock more potential, and prepare for tougher encounters."], ["04", "⚔", "Battle", "Put your party to the test and prove what your Mojumon team can do."]].map(([no, icon, h, p], i) => (
                  <div key={no} className={c("loop-step")}>
                    <span className={c("step-no")}>STEP {no}</span>
                    <div className={c("loop-icon")}>{icon}</div>
                    <h3>{h}</h3>
                    <p>{p}</p>
                    {i < 3 && <span className={c("loop-arrow")}>›</span>}
                  </div>))}
              </div>
            </div>
          </section>

          <section className={c("section", "dojo")} id="tamers">
            <div className={c("section-inner")}>
              <div className={c("tamer-intro-panel")}>
                <p className={c("section-kicker", "pixel-font")}>Choose your look</p>
                <h2>Meet the Crimson Tamers</h2>
                <p className={c("section-intro")}>Step through the full Dojo roster and find the Tamer you want to become.</p>
                <div className={c("cj-route")} aria-label="8-bit SenseiCJ walking back and forth along a field route">
                  <div className={c("cj-patrol")} aria-hidden="true"><span className={c("cj-walker")} /></div>
                </div>
              </div>
              <Tamers />
              <div className={c("feature-strip")}>
                <div className={c("feature")}><b>A Living Roster</b><span>Discover Mojumon with different looks, strengths, and personalities.</span></div>
                <div className={c("feature")}><b>Your Kind of Team</b><span>Train the creatures you love and shape a party that feels like yours.</span></div>
                <div className={c("feature")}><b>Made for the Dojo</b><span>A monster-taming adventure built from the characters and culture of the community.</span></div>
              </div>
            </div>
          </section>

          <section className={c("section", "threat")} aria-labelledby="threatTitle">
            <div className={c("section-inner")}>
              <div className={c("threat-file")}>
                <div className={c("threat-copy")}>
                  <span className={c("threat-code")}>Threat File // Crimson Squad</span>
                  <p className={c("section-kicker", "pixel-font")}>Main antagonists detected</p>
                  <h2 id="threatTitle">Rush &amp; Roulette</h2>
                  <p>The Crimson Squad’s most dangerous duo stands between the Tamers and their goal. Rush strikes fast and thinks faster; Roulette brings the muscle, menace, and a grin that says trouble has already arrived.</p>
                  <div className={c("threat-tags")}><span>Crimson Squad</span><span>Major Rivals</span><span>High Threat</span></div>
                </div>
                <div className={c("threat-art")}><img src={ART.threat} alt="Rush and Roulette of the Crimson Squad in 16-bit pixel art" /></div>
              </div>
            </div>
          </section>

          <FusionLab />

          <section className={c("cta")} id="beta">
            <p className={c("section-kicker", "pixel-font")}>The Crimson journey begins</p>
            <h2>Your First Mojumon Is Waiting.</h2>
            <p>Choose your starter. Build your party. Become the Tamer everyone remembers.</p>
            <a className={c("btn", "btn-primary")} href={BETA_URL} target="_blank" rel="noopener noreferrer">Enter Mojumon Crimson <span>▶</span></a>
            <p className={c("fineprint")}>Beta edition • Built for the Dojo</p>
          </section>
        </main>
        <footer className={c("footer")}>© 2026 Mojumon Crimson • Capture • Train • Conquer</footer>
      </div>

    </div>
  );
}
